/* Quality targets, read from the contracts that carry them.
 *
 * One request for the whole page, and the projection into a per-dataset target is arithmetic. That
 * is the shape this screen needs — a target on every row, with no request per row.
 *
 * **A failed read is not "no targets".** `available` is false when the contracts read failed, and
 * the ledger renders "target unknown" rather than "no target set" — the second is a statement about
 * the tenant's governance made out of a failed request.
 *
 * **Nor is a truncated read.** `GET /v1/contracts` is `ORDER BY created_at DESC LIMIT n` with no
 * cursor, so a tenant past the cap cannot reach the rest — and a dataset whose contract fell past
 * that boundary would render as "no target set" while a signed promise binds it. `complete` carries
 * the envelope's own `truncated` so the screen states the bound rather than asserting an estate.
 * This file used to claim the route "returns every contract this tenant holds", which was never
 * true and is the mechanism-that-does-not-exist class CLAUDE.md §9 records.
 */

import { useContracts } from '@/features/contracts';

import { targetsByDataset } from '../model/targets';
import type { QualityTarget } from '../model/types';

export interface QualityTargets {
  readonly byDataset: ReadonlyMap<string, QualityTarget>;
  readonly loading: boolean;
  /** False when the contracts read failed. Distinct from an empty map, which means the tenant has
   * published no contract carrying a score floor — a real and common state. */
  readonly available: boolean;
  /** False when the contracts listing was capped, so a row with no target may still be bound by a
   * contract this page did not carry. Distinct from `available`: the read succeeded and what came
   * back is true, it is simply not all of it. */
  readonly complete: boolean;
  readonly error: Error | null;
}

const NONE: ReadonlyMap<string, QualityTarget> = new Map();

export function useQualityTargets(): QualityTargets {
  const contracts = useContracts();

  return {
    byDataset:
      contracts.data === undefined ? NONE : targetsByDataset(contracts.data.items),
    loading: contracts.isPending,
    available: !contracts.isError,
    /* Unread is not incomplete: an unread listing is already reported by `available`, and saying
     * both would put two different sentences under one failure. */
    complete: !contracts.data?.truncated,
    error: contracts.isError ? contracts.error : null,
  };
}
